import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../../components/hotels/Header";
import SearchForm from "../../components/hotels/SearchForm";
import StayCard from "../../components/hotels/StayCard";
import Filters from "../../components/hotels/Filters";
import { config } from "../../config";
import LoadingSkeleton from "../../components/hotels/LoadingSkeleton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMap } from "@fortawesome/free-solid-svg-icons";
import Map from "../../components/hotels/Map";

function HotelsSearchResults() {
  const [searchParams] = useSearchParams();
  const [stays, setStays] = useState([]);
  const [filteredStays, setFilteredStays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMap, setShowMap] = useState(false);

  const initialLocation = searchParams.get("locationName") || "";

  // Fetch Hotels
  useEffect(() => {
    const fetchStays = async () => {
      try {
        setLoading(true);
        setError(null);

        const searchData = {
          latitude: searchParams.get("latitude"),
          longitude: searchParams.get("longitude"),
          radius: searchParams.get("radius") || 5,
          checkIn: searchParams.get("checkIn"),
          checkOut: searchParams.get("checkOut"),
          rooms: parseInt(searchParams.get("rooms") || "1"),
          adults: parseInt(searchParams.get("adults") || "1"),
          children: parseInt(searchParams.get("children") || "0"),
          childrenAges: searchParams.get("childrenAges") || "",
          currency: searchParams.get("currency") || "GBP",
        };

        console.log("🔍 Search Request:", searchData);

        const response = await fetch(
          `${config.backendServer}/api/hotels/search`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(searchData),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to fetch hotels");
        }

        const data = await response.json();
        console.log("📥 Search Response:", data);

        setStays(data.data.results);
        setFilteredStays(data.data.results); // Initialize filtered results with all results
      } catch (err) {
        console.error("❌ Error fetching stays:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStays();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-slate-800">
      <Header isTransparent={true} />
      <div className="container mx-auto py-8">
        <div className="mb-8">
          <SearchForm
            initialLocation={initialLocation}
            initialDateRange={[
              new Date(searchParams.get("checkIn")),
              new Date(searchParams.get("checkOut")),
            ]}
            initialGuests={{
              adults: parseInt(searchParams.get("adults") || "1"),
              children: parseInt(searchParams.get("children") || "0"),
            }}
            initialRooms={parseInt(searchParams.get("rooms") || "1")}
          />
        </div>

        <div className="flex flex-col md:flex-row lg:flex-row gap-4">
          <div className="md:w-1/4 lg:w-1/4">
            <div className="w-full mb-4 overflow-hidden rounded-lg border border-gray-200">
              <button
                onClick={() => setShowMap(!showMap)}
                className={`w-full ${config.primaryColorBg} text-gray-800 p-3 flex items-center justify-center gap-2 hover:bg-opacity-90 transition-colors`}
              >
                <FontAwesomeIcon icon={faMap} />
                {showMap ? "Hide Map" : "Show Map"}
              </button>
              <div className="w-full h-32 relative">
                <img
                  src="/map.webp"
                  alt="Map preview"
                  className="w-full h-full object-cover"
                />
                <div
                  className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all cursor-pointer"
                  onClick={() => setShowMap(true)}
                />
              </div>
            </div>

            {!loading && !error && stays.length > 0 && (
              <Filters results={stays} setFilteredResults={setFilteredStays} />
            )}
          </div>

          <div className="flex-1">
            {loading && <LoadingSkeleton />}

            {error && (
              <div className="text-center py-8">
                <div className="bg-red-50 rounded-lg p-4 mb-4">
                  <p className="text-red-600 font-medium">{error}</p>
                  <p className="text-red-500 text-sm mt-1">
                    Please try adjusting your search criteria or try again
                    later.
                  </p>
                </div>
              </div>
            )}

            {!loading && !error && filteredStays.length > 0 && (
              <div>
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-white">
                    {filteredStays.length}{" "}
                    {filteredStays.length === 1 ? "property" : "properties"}{" "}
                    found
                  </h2>
                </div>

                {filteredStays.map((stay) => (
                  <StayCard key={stay.id} stay={stay} />
                ))}
              </div>
            )}

            {!loading && !error && filteredStays.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-300">
                  No hotels found matching your filters.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {stays && stays.length > 0 && (
        <Map
          stays={filteredStays}
          isOpen={showMap}
          onClose={() => setShowMap(false)}
        />
      )}
    </div>
  );
}

export default HotelsSearchResults;
